import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Toaster } from "sonner";
import "./App.css";

import { AuthProvider } from "./lib/auth";
import { CartProvider } from "./lib/cart";

import Header from "./components/Header";
import Footer from "./components/Footer";
import WelcomePopup from "./components/WelcomePopup";
import AgeGate, { getAgeVerified } from "./components/AgeGate";

import HomePage from "./pages/HomePage";
import ShopPage from "./pages/ShopPage";
import ProductPage from "./pages/ProductPage";
import CartPage from "./pages/CartPage";
import CheckoutPage from "./pages/CheckoutPage";
import AccountPage from "./pages/AccountPage";
import CoaLibrary from "./pages/CoaLibrary";
import QualityPage from "./pages/QualityPage";
import ResearchCenter from "./pages/ResearchCenter";
import LabelDesigner from "./pages/LabelDesigner";
import AdminPage from "./pages/AdminPage";
import ContactPage from "./pages/ContactPage";
import AboutPage from "./pages/AboutPage";
import CookieBanner from "./components/CookieBanner";
import RequireAuth from "./components/RequireAuth";
import OrderSuccessPage from "./pages/OrderSuccessPage";
import PaymentCompletePage from "./pages/PaymentCompletePage";
import PolicyPage from "./pages/PolicyPage";
import { BlogIndex, BlogPost } from "./pages/BlogPage";
import { LoginPage, RegisterPage, ForgotPasswordPage, ResetPasswordPage } from "./pages/AuthPages";

function ScrollToTop() {
    const { pathname } = useLocation();
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
    }, [pathname]);
    return null;
}

export default function App() {
    const [verified, setVerified] = useState(() => getAgeVerified());

    return (
        <BrowserRouter>
            <AuthProvider>
                <CartProvider>
                    {!verified && <AgeGate onPass={() => setVerified(true)} />}
                    <ScrollToTop />
                    <Toaster
                        position="bottom-right"
                        toastOptions={{
                            style: {
                                background: "var(--bg-carbon)",
                                border: "1px solid var(--border)",
                                color: "var(--text-primary)",
                                borderRadius: 0,
                                fontFamily: "JetBrains Mono, monospace",
                                fontSize: "0.75rem",
                            },
                        }}
                    />
                    <Header />
                    <main className="min-h-screen">
                        <Routes>
                            <Route path="/" element={<HomePage />} />
                            <Route path="/shop" element={<RequireAuth><ShopPage /></RequireAuth>} />
                            <Route path="/product/:slug" element={<RequireAuth><ProductPage /></RequireAuth>} />
                            <Route path="/cart" element={<CartPage />} />
                            <Route path="/checkout" element={<CheckoutPage />} />
                            <Route path="/order/:id" element={<OrderSuccessPage />} />
                            <Route path="/payment/complete" element={<PaymentCompletePage />} />
                            <Route path="/coa" element={<CoaLibrary />} />
                            <Route path="/quality" element={<QualityPage />} />
                            <Route path="/research" element={<ResearchCenter />} />
                            <Route path="/contact" element={<ContactPage />} />
                            <Route path="/about" element={<AboutPage />} />
                            <Route path="/terms" element={<PolicyPage doc="terms" />} />
                            <Route path="/privacy" element={<PolicyPage doc="privacy" />} />
                            <Route path="/shipping" element={<PolicyPage doc="shipping" />} />
                            <Route path="/refund" element={<PolicyPage doc="refund" />} />
                            <Route path="/compliance" element={<PolicyPage doc="compliance" />} />
                            <Route path="/blog" element={<BlogIndex />} />
                            <Route path="/blog/:slug" element={<BlogPost />} />
                            <Route path="/label-designer" element={<LabelDesigner />} />
                            <Route path="/account/*" element={<AccountPage />} />
                            <Route path="/admin/*" element={<AdminPage />} />
                            <Route path="/login" element={<LoginPage />} />
                            <Route path="/register" element={<RegisterPage />} />
                            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                            <Route path="/reset-password" element={<ResetPasswordPage />} />
                        </Routes>
                    </main>
                    <Footer />
                    <ConditionalWelcomePopup />
                    <CookieBanner />
                </CartProvider>
            </AuthProvider>
        </BrowserRouter>
    );
}

function ConditionalWelcomePopup() {
    const { pathname } = useLocation();
    // Suppress the welcome/coupon popup on the order confirmation page so it
    // doesn't dilute the post-payment confirmation moment, and on /checkout
    // so its backdrop doesn't intercept clicks on the address autocomplete.
    if (pathname.startsWith("/order/")) return null;
    if (pathname.startsWith("/payment/")) return null;
    if (pathname.startsWith("/checkout")) return null;
    return <WelcomePopup />;
}
